import { GetNewAttributeValue } from './GetNewAttributeValue.js';
import { SquashNulls } from './SquashNulls.js';
import { PickAnAttribute } from './PickAnAttribute.js';
import { Path } from './Path.js';

/**
 *
 * options
 *    newValue
 *    oldValue
 *    update
 *    updatePath
 *    singularAttributes - boolean defaults to false. (set to true for things like job attributes)
 */
export function AddNodeCalls(property, itemAttributes, otherAttributes, options) {
  options = options || {};

  let newValueModel = null;
  let oldValueModel = null;
  let picklists = SquashNulls(otherAttributes, property, 'picklists');
  let elemsToIgnore = options.ignore || [];

  // If the property is a multi_dropdown, set an option flag
  if (otherAttributes?.[property]?.gui_element == 'multi_dropdown') options.isMultiDropdown = true;
  else options.isMultiDropdown = false;

  const clearValue = function (attribute, itemKey = '') {
    let attributePath = attribute + (options.singularAttributes ? '' : '/' + itemKey);
    if (options.update) {
      // Don't clear other calls being set
      if (typeof options.update[(options.updatePath || '') + attributePath] == 'undefined') {
        options.update[(options.updatePath || '') + attributePath] = null;
        Path.delete(itemAttributes, attributePath, '/');
      }
    } else {
      Path.delete(itemAttributes, attributePath, '/');
    }
  };

  const updateCalls = function (item, forceCallCheck) {
    // If the property is a multi_dropdown, get the new value(s)
    let multiDropdownNewValues = null;
    if (options.isMultiDropdown) multiDropdownNewValues = Object.values(options.newValue ?? {});

    if (
      (options.hasOwnProperty('newValue') &&
        item.hasOwnProperty('value') &&
        (item.value == options.newValue || multiDropdownNewValues?.includes(item.value))) ||
      forceCallCheck == true
    ) {
      newValueModel = item;
      if (item._call) {
        for (let call of item._call) {
          let value = '';
          let attribute = call;
          if (typeof call == 'object') {
            // Use value, unless $now is present, then use current time
            value = (call.value == '$now' ? Date.now() : call.value) || '';
            attribute = call.attribute;
          }
          if (value == '') {
            if (SquashNulls(itemAttributes, attribute, 'button_added') != '') {
              continue;
            }
            value = GetNewAttributeValue(attribute, otherAttributes);
          }
          // If call attribute is set to persist, and that call attribute already exists
          if (call.should_persist && PickAnAttribute(itemAttributes, attribute)) return;
          let setCall = true;
          if (call.set_if) {
            let pathSplit = call.set_if.path.split('.');
            let prop = pathSplit.shift();
            let attributeValues = Object.values(SquashNulls(itemAttributes, prop));
            let val = Path.get(attributeValues, pathSplit.join('.'));
            setCall = val == call.set_if.value;
          }
          if (setCall) {
            let itemKey = 'call_added';
            if (call.use_existing_key) {
              let firstExistingKey = Object.keys(itemAttributes?.[attribute])?.[0];
              if (firstExistingKey) {
                itemKey = firstExistingKey;
              }
            }
            // If the attribute is a multi_dropdown, then don't set an item key because each
            // key needs to be an instance of a value, not an instance of an attribute
            if (otherAttributes?.[attribute]?.gui_element == 'multi_dropdown') {
              itemKey = '';
            }
            let attributePath = attribute + (options.singularAttributes ? '' : '/' + itemKey);
            Path.set(itemAttributes, attributePath, value, '/');
            if (options.update && !elemsToIgnore.includes(attribute)) {
              options.update[(options.updatePath || '') + attributePath] = value;
            }
          }
        }
      }
    }
  };

  const clearCalls = function (item) {
    // If the property is a multi_dropdown, get the new value(s)
    let multiDropdownOldValues = null;
    if (options.isMultiDropdown) multiDropdownOldValues = Object.values(options.oldValue ?? {});

    if (
      options.hasOwnProperty('oldValue') &&
      item.hasOwnProperty('value') &&
      (item.value == options.oldValue || multiDropdownOldValues?.includes(item.value))
    ) {
      oldValueModel = item;
      if (item._call) {
        item._call.forEach((call) => {
          // If call attribute is set to persist, don't remove it
          if (call.should_persist) return;
          let attribute = call;
          if (typeof call == 'object') {
            attribute = call.attribute;
          }
          if (attribute != '' && !elemsToIgnore.includes(attribute) && !call.no_clear) {
            // If the attribute is a multi_dropdown, then clear the root of the attribute, not specific keys because they won't exist
            if (otherAttributes?.[attribute]?.gui_element == 'multi_dropdown') {
              clearValue(attribute);
            } else {
              clearValue(attribute, 'call_added');
              // If the user has configured the button to add an attribute that is also a call, they want it to really be a call, so clear it
              clearValue(attribute, 'button_added');
            }
          }
        });
      }
    }
  };

  // Loop through the items in the picklists and add attributes
  // found in the _call object for the selected value
  for (let picklistId in picklists) {
    for (let item of picklists[picklistId]) {
      updateCalls(item);
    }
  }
  // Also check if the attribute has a _call object at the root of the attribute model (this
  // was first created for job-level attributes so that changing a non-picklist attribute could
  // update a date attribute) and create a fake item with a _call to force the update
  if (SquashNulls(otherAttributes, property, '_call')) {
    updateCalls(SquashNulls(otherAttributes, property), true);
  }

  // Loop through the items in the picklists and remove attributes
  // found in the _call object for the previous value
  for (let picklistId in picklists) {
    for (let item of picklists[picklistId]) {
      clearCalls(item);
    }
  }
  // Also check if the attribute has a _call object at the root of the attribute model (this
  // was first created for job-level attributes so that changing a non-picklist attribute could
  // update a date attribute) and create a fake item with a _call to force the update
  if (SquashNulls(otherAttributes, property, '_call')) {
    clearCalls(SquashNulls(otherAttributes, property));
  }

  return { newValueModel, oldValueModel };
}
